// src/pages/admin/emails/AdminEmailTestBoard.tsx
import { useEffect, useState } from "react";
import { supabase } from "../../../lib/supabaseClient";
import { Link } from "react-router-dom";
import { ENV } from "../../../env";

function deriveFunctionEndpoint(): string {
  if (ENV.FUNCTION_ENDPOINT) return ENV.FUNCTION_ENDPOINT;
  if (ENV.SUPABASE_URL) return `${ENV.SUPABASE_URL.replace(/\/+$/, "")}/functions/v1`;

  const restUrl: string | undefined = (supabase as any)?.rest?.url;
  if (restUrl) {
    const projectBase = restUrl.replace(/\/rest\/v1\/?$/, "");
    return `${projectBase}/functions/v1`;
  }

  return "";
}

type ConfigRow = { key: string; value: any };

export default function AdminEmailTestBoard() {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState<Record<string, any>>({});
  const [logs, setLogs] = useState<string[]>([]);
  const [lastResult, setLastResult] = useState<any>(null);

  const FUNCTION_ENDPOINT = deriveFunctionEndpoint();

  async function callFn(path: string, params?: Record<string, any>) {
    if (!FUNCTION_ENDPOINT) {
      setLogs((l) => [
        "FUNCTION_ENDPOINT ist leer - prüfe ENV (VITE_ oder REACT_APP_)",
        ...l,
      ]);
      return { ok: false, status: 0, data: { error: "missing FUNCTION_ENDPOINT" } };
    }

    const qs =
      params && Object.keys(params).length
        ? "?" +
          new URLSearchParams(
            Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
          ).toString()
        : "";

    const { data: sessionData } = await supabase.auth.getSession();
    const jwt = sessionData?.session?.access_token || ENV.ANON_KEY;

    const url = `${FUNCTION_ENDPOINT}/${path}${qs}`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });

    const text = await res.text();
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { raw: text };
    }

    setLastResult(parsed);
    setLogs((l) => [`${res.ok ? "OK" : "FEHLER"} GET ${url} -> ${res.status}`, ...l]);
    return { ok: res.ok, status: res.status, data: parsed };
  }

  async function loadConfig() {
    setLoading(true);
    const { data, error } = await supabase.from("app_config").select("key,value");
    if (!error) {
      const map: Record<string, any> = {};
      (data ?? []).forEach((r: ConfigRow) => (map[r.key] = r.value));
      setConfigs(map);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadConfig();
  }, []);

  async function updateConfig(key: string, value: any) {
    const { error } = await supabase
      .from("app_config")
      .upsert({ key, value }, { onConflict: "key" });
    if (error) alert(error.message);
    else {
      setLogs((l) => [`Konfiguration: ${key} -> ${JSON.stringify(value)}`, ...l]);
      loadConfig();
    }
  }

  function current(key: string, fallback: any = null) {
    return configs[key] ?? fallback;
  }

  async function runDryRun() {
    await callFn("email-evaluator-dry-run", {
      hours: 6,
      limit: 20,
      only_beta: current("email_queue_only_beta", true),
    });
  }

  async function runQueueWrite() {
    await callFn("email-evaluator-queue", {
      hours: 6,
      limit: 20,
      only_beta: current("email_queue_only_beta", true),
    });
  }

  async function runSender() {
    await callFn("email-sender", { limit: 5 });
  }

  function section(title: string, children: any) {
    return (
      <div className="p-4 border border-[#2a3347] rounded-xl bg-[#161b27] space-y-2">
        <h2 className="font-semibold text-lg text-sky-400">{title}</h2>
        {children}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6 space-y-6">
      <h1 className="text-3xl font-bold text-sky-400 mb-6">Test-Board E-Mail-System</h1>

      {loading ? (
        <div className="text-slate-400">Lade aktuelle Konfiguration...</div>
      ) : (
        <>
          {section(
            "Modus & Master-Switch",
            <div className="space-x-2">
              <span className="text-slate-300">Aktueller Modus:</span>
              <strong className="text-slate-100">{current("email_mode", "—")}</strong>
              <div className="flex gap-2 mt-2">
                {["dry_run", "sandbox", "limited_prod", "full_prod"].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => updateConfig("email_mode", mode)}
                    className={`px-3 py-1 rounded border transition-colors ${
                      current("email_mode") === mode
                        ? "bg-sky-500 text-white border-sky-500"
                        : "bg-[#1e2639] text-slate-300 border-[#2a3347] hover:bg-[#2a3347]"
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
          )}

          {section(
            "Queue-Writes",
            <div className="flex items-center gap-3">
              <span className="text-slate-300">
                Status: {String(current("email_queue_writes_enabled", "false"))}
              </span>
              <button
                onClick={() =>
                  updateConfig(
                    "email_queue_writes_enabled",
                    !(current("email_queue_writes_enabled") === true)
                  )
                }
                className="px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
              >
                Toggle
              </button>
            </div>
          )}

          {section(
            "Whitelist & Sandbox",
            <div className="space-y-2">
              <div>
                <span className="text-slate-300">Nur Beta?</span>{" "}
                <strong className="text-slate-100">
                  {String(current("email_queue_only_beta", "false"))}
                </strong>
                <button
                  onClick={() =>
                    updateConfig(
                      "email_queue_only_beta",
                      !(current("email_queue_only_beta") === true)
                    )
                  }
                  className="ml-2 px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
                >
                  Toggle
                </button>
              </div>
              <div className="text-slate-300">
                Sandbox-Recipient:{" "}
                <input
                  type="text"
                  defaultValue={current("email_sandbox_recipient", "")}
                  className="border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded px-2 py-1 outline-none focus:border-sky-400"
                  onBlur={(e) => updateConfig("email_sandbox_recipient", e.target.value)}
                />
              </div>
            </div>
          )}

          {section(
            "Tages-Cap Default",
            <div className="flex items-center gap-3">
              <span className="text-slate-100">{current("email_daily_cap_default", 0)}</span>
              <button
                onClick={() => updateConfig("email_daily_cap_default", 0)}
                className="px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
              >
                0 (kein Versand)
              </button>
              <button
                onClick={() => updateConfig("email_daily_cap_default", 1)}
                className="px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
              >
                1 / Tag
              </button>
              <button
                onClick={() => updateConfig("email_daily_cap_default", 10)}
                className="px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
              >
                10 / Tag
              </button>
            </div>
          )}

          {section(
            "Aktionen (mit Auth-Header)",
            <div className="flex flex-wrap gap-3">
              <button
                onClick={runDryRun}
                className="px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
              >
                Dry-Run Evaluator
              </button>
              <button
                onClick={runQueueWrite}
                className="px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
              >
                Queue-Write Evaluator
              </button>
              <button
                onClick={runSender}
                className="px-3 py-1 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
              >
                Sender starten
              </button>
            </div>
          )}

          {section(
            "Result (die letzte Function-Antwort)",
            <pre className="text-xs bg-[#0d1117] border border-[#2a3347] p-2 rounded max-h-60 overflow-auto text-slate-300">
              {lastResult ? JSON.stringify(lastResult, null, 2) : "—"}
            </pre>
          )}

          {section(
            "Diagnose",
            <div className="text-xs text-slate-300 space-y-1">
              <div>
                <b>FUNCTION_ENDPOINT:</b> {FUNCTION_ENDPOINT || "— (leer)"}
              </div>
              <div>
                <b>ENV.FUNCTION_ENDPOINT:</b> {ENV.FUNCTION_ENDPOINT || "—"}
              </div>
              <div>
                <b>ENV.SUPABASE_URL:</b> {ENV.SUPABASE_URL || "—"}
              </div>
              <div>
                <b>supabase.rest.url:</b> {(supabase as any)?.rest?.url || "—"}
              </div>
              <div className="text-slate-500">
                Tipp: Wenn oben vieles "—" ist, fehlt ENV oder der Client wurde falsch initialisiert.
              </div>
            </div>
          )}

          {section(
            "Logs",
            <div className="text-xs bg-[#0d1117] border border-[#2a3347] p-2 rounded max-h-40 overflow-auto space-y-1 text-slate-300">
              {logs.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>
          )}

          <div className="p-4 border border-[#2a3347] rounded-xl bg-[#161b27]">
            <h2 className="font-semibold text-lg text-sky-400 mb-2">Kurzanleitung (GUI-Test)</h2>
            <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-300">
              <li>Template &amp; Regel anlegen.</li>
              <li>
                Mode <strong>sandbox</strong>, Queue-Writes <strong>true</strong>, Nur Beta{" "}
                <strong>true</strong>, Cap <strong>1</strong>.
              </li>
              <li>
                Neuen User registrieren (ohne Trip) &amp; als{" "}
                <em>email_beta</em> markieren.
              </li>
              <li>
                <em>Queue-Write Evaluator</em> &rarr; Queue prüfen &rarr; "Jetzt senden".
              </li>
              <li>
                <em>Sender starten</em> &rarr; Sandbox-Postfach prüfen, Historie &amp; Events
                ansehen.
              </li>
            </ol>
          </div>
        </>
      )}

      <div className="mt-8">
        <Link to="/admin/emails" className="text-sky-400 hover:underline">
          &larr; Zurück zum E-Mail-Bereich
        </Link>
      </div>
    </div>
  );
}
